import React, { useState } from "react";
import { Select, Tag, DatePicker } from "antd";
import { THEME_PALLETE } from "../../Theme";
import {
  GitBranch,
  CalendarDays,
  Layers,
  Tag as TagIcon,
  ShieldCheck,
} from "lucide-react";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";

const { Option } = Select;
const { RangePicker } = DatePicker;

const MediaFilter = ({
  onFilterChange = () => {},
  mode = "media",
  branches = [],
}) => {
  const { lang } = useProLanguage();
  const t = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).media;
  const [filters, setFilters] = useState({
    usage: [],
    expiry: [],
    dateRange: null, // Store selected date range
    branches: ["all"], // Default to All Branches
    createLevel: "all", // Default to All Levels
  });

  const handleFilterChange = (key, value) => {
    let newFilters = { ...filters };
    if (key === "branches") {
      // If "all" is in the new value and wasn't before, select only "all"
      if (value.includes("all") && !filters.branches.includes("all")) {
        newFilters.branches = ["all"];
      }
      // If "all" was there but other options are selected, remove "all"
      else if (filters.branches.includes("all") && value.length > 1) {
        newFilters.branches = value.filter((v) => v !== "all");
      } else if (value.length === 0) {
        // If empty, auto-select "all"
        newFilters.branches = ["all"];
      } else {
        newFilters.branches = value;
      }
    } else {
      newFilters[key] = value;
    }

    setFilters(newFilters);
    onFilterChange(newFilters); // Notify parent
  };

  const getStatusTag = (label, type) => {
    const colorMap = {
      usage: { [t.filterInUse]: "blue", [t.filterNotInUse]: "orange" },
      expiry: { [t.filterActive]: "green", [t.filterExpired]: "red" },
    };
    return (
      <Tag color={THEME_PALLETE[(colorMap[type] || {})[label] || ""]}>
        {label}
      </Tag>
    );
  };

  return (
    <div className="media-filter-bar">
      {/* CreateLevel Filter */}
      <div className="media-filter-item">
        <label className="media-filter-label">
          <span
            className="media-filter-label-icon"
            style={{
              background: "linear-gradient(135deg,#fffbeb,#fef3c7)",
              border: "1px solid #fde68a",
            }}
          >
            <Layers size={12} color="#b45309" />
          </span>
          {t.filterOwnership}
        </label>
        <Select
          size="middle"
          className="media-filter-select"
          style={{ width: "100%" }}
          value={filters.createLevel}
          onChange={(value) => handleFilterChange("createLevel", value)}
        >
          <Option value="all">{t.filterAllContent}</Option>
          <Option value="1">{t.filterBrandContent}</Option>
          <Option value="2">{t.filterBranchContent}</Option>
        </Select>
      </div>

      {/* Branch Filter */}
      {branches.length > 0 && (
        <div className="media-filter-item media-filter-item--wide">
          <label className="media-filter-label">
            <span
              className="media-filter-label-icon"
              style={{
                background: "linear-gradient(135deg,#eff6ff,#dbeafe)",
                border: "1px solid #bfdbfe",
              }}
            >
              <GitBranch size={12} color="#2563eb" />
            </span>
            {t.filterBranches}
          </label>
          <Select
            allowClear
            mode="multiple"
            size="middle"
            className="media-filter-select"
            style={{ width: "100%" }}
            value={filters.branches}
            onChange={(value) => handleFilterChange("branches", value)}
            maxTagCount="responsive"
          >
            <Option key="all" value="all">
              {t.filterAllBranches}
            </Option>
            {branches.map((b) => (
              <Option key={b.BranchID} value={String(b.BranchID)}>
                {b.BranchName}
              </Option>
            ))}
          </Select>
        </div>
      )}

      {/* Date Range Filter */}
      <div className="media-filter-item media-filter-item--date">
        <label className="media-filter-label">
          <span
            className="media-filter-label-icon"
            style={{
              background: "linear-gradient(135deg,#f0fdf4,#dcfce7)",
              border: "1px solid #86efac",
            }}
          >
            <CalendarDays size={12} color="#16a34a" />
          </span>
          {mode === "trash" ? t.filterDeleteDate : t.filterAddDate}
        </label>
        <RangePicker
          allowClear
          size="middle"
          className="media-filter-rangepicker"
          style={{ width: "100%" }}
          placeholder={[t.filterDateStart, t.filterDateEnd]}
          value={filters.dateRange}
          onChange={(dates) => handleFilterChange("dateRange", dates || null)}
        />
      </div>

      {/* Usage Filter */}
      {mode !== "trash" && mode !== "playlist" && (
        <div className="media-filter-item">
          <label className="media-filter-label">
            <span
              className="media-filter-label-icon"
              style={{
                background: "linear-gradient(135deg,#faf5ff,#ede9fe)",
                border: "1px solid #ddd6fe",
              }}
            >
              <TagIcon size={12} color="#7c3aed" />
            </span>
            {t.filterUsage}
          </label>
          <Select
            placeholder="All"
            allowClear
            mode="multiple"
            size="middle"
            className="media-filter-select"
            style={{ width: "100%" }}
            value={filters.usage}
            onChange={(value) => handleFilterChange("usage", value)}
          >
            <Option value={1}>{getStatusTag(t.filterInUse, "usage")}</Option>
            <Option value={0}>{getStatusTag(t.filterNotInUse, "usage")}</Option>
          </Select>
        </div>
      )}

      {/* Expiry Filter */}
      {mode !== "trash" && mode !== "playlist" && (
        <div className="media-filter-item">
          <label className="media-filter-label">
            <span
              className="media-filter-label-icon"
              style={{
                background: "linear-gradient(135deg,#f0fdf4,#dcfce7)",
                border: "1px solid #86efac",
              }}
            >
              <ShieldCheck size={12} color="#16a34a" />
            </span>
            {t.filterExpiry}
          </label>
          <Select
            placeholder="All"
            allowClear
            mode="multiple"
            size="middle"
            className="media-filter-select"
            style={{ width: "100%" }}
            value={filters.expiry}
            onChange={(value) => handleFilterChange("expiry", value)}
          >
            <Option value={1}>{getStatusTag(t.filterActive, "expiry")}</Option>
            <Option value={0}>{getStatusTag(t.filterExpired, "expiry")}</Option>
          </Select>
        </div>
      )}
    </div>
  );
};

export default MediaFilter;
